import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { SIDE_NAV_MENU_ITEMS, SIDE_NAV_MENU_ITEMS_GLOBAL_ADMIN, SideNavMenuItem } from './const/sidenav-menu.const';
import { AuthService } from './services/auth.service';
import { UserRole } from './shared/models/user.model';

@Component({
    imports: [
        CommonModule,
        RouterOutlet,
        FormsModule,
        ReactiveFormsModule,
        MatToolbarModule,
        MatSidenavModule,
        MatIconModule,
        MatButtonModule,
        MatCardModule,
        MatCheckboxModule,
        MatInputModule,
        MatTableModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatOptionModule,
        MatSelectModule,
        MatExpansionModule,
        MatDividerModule,
        MatSlideToggleModule,
        MatListModule,
        RouterModule,
        AsyncPipe,
    ],
    providers: [AuthService],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    @ViewChild('sidenav') sidenav!: MatSidenav;
    public isLoggedIn$!: Observable<boolean>;
    public userEmail: string | null = '';
    private readonly unsubscribe$: Subject<void>;
    public readonly sideNavMenuItemGlobalAdmin: SideNavMenuItem[] = SIDE_NAV_MENU_ITEMS_GLOBAL_ADMIN;
    public readonly currentRole$: Observable<string | null>;
    public filteredSideNavMenuItems$!: Observable<SideNavMenuItem[]>;
    public showGlobalMenu!: Observable<boolean>;

    constructor(
        private readonly authService: AuthService,
        private readonly router: Router
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.currentRole$ = this.authService.getAuthenticatedUserRole();
        this.initMenuObservables();
    }

    private initMenuObservables(): void {
        this.showGlobalMenu = this.currentRole$.pipe(map((role) => role === UserRole.SUPER_ADMIN));
        this.filteredSideNavMenuItems$ = this.currentRole$.pipe(
            map((role) => {
                const validRole = Object.values(UserRole).includes(role as UserRole) ? (role as UserRole) : null;

                return SIDE_NAV_MENU_ITEMS.filter(
                    (item) =>
                        !item.allowedRoles?.length || (validRole !== null && item.allowedRoles.includes(validRole))
                );
            })
        );
    }

    public ngOnInit(): void {
        this.isLoggedIn$ = this.authService.isLoggedIn$();

        this.authService
            .getAuthenticatedUserEmail()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((email) => {
                this.userEmail = email;
            });
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public toggleSidenav(): void {
        this.sidenav?.toggle();
    }

    public onLogout(): void {
        this.authService.logout();
        this.router.navigate(['/login']);
    }

    public isActiveRoute(url: string): boolean {
        return this.router.url.slice(1) === url;
    }
}
